import React, { useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';

import { Alert, Box, Button, CircularProgress, Container, Typography } from '@mui/material';

import TagsService, { type TagDataExportType } from '~/services/TagsService';

type ExportState =
  | { status: 'loading' }
  | { status: 'complete'; downloadUrl: string }
  | { status: 'error'; message: string };

export default function TagDataExport() {
  const [state, setState] = useState<ExportState>({ status: 'loading' });
  const { search } = useLocation();
  const tagType = new URLSearchParams(search).get('tagType');

  useEffect(() => {
    let downloadUrl: string | undefined;

    const exportData = async () => {
      try {
        if (tagType !== 'passport') {
          throw new Error('A supported tag type is required to export tags data.');
        }

        const csv = await TagsService.downloadTagData(tagType as TagDataExportType);
        downloadUrl = URL.createObjectURL(csv);
        setState({ status: 'complete', downloadUrl });
      } catch (error) {
        setState({
          status: 'error',
          message: error instanceof Error ? error.message : 'Unable to export Passport tags data.',
        });
      }
    };

    void exportData();

    return () => {
      if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    };
  }, [tagType]);

  return (
    <Container sx={{ py: 6 }}>
      <Typography variant="h5" gutterBottom>
        Export Tags Data
      </Typography>
      {state.status === 'loading' && (
        <Box display="flex" alignItems="center" gap={2}>
          <CircularProgress size={24} />
          <Typography>Preparing Passport tags data…</Typography>
        </Box>
      )}
      {state.status === 'complete' && (
        <Box display="flex" flexDirection="column" gap={2} alignItems="flex-start">
          <Alert severity="success">Your Passport tags data is ready.</Alert>
          <Button
            component="a"
            href={state.downloadUrl}
            download="passport-experience-hotel-offer-associations.csv"
            variant="contained"
          >
            Download CSV
          </Button>
        </Box>
      )}
      {state.status === 'error' && <Alert severity="error">{state.message}</Alert>}
    </Container>
  );
}
